// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo } from 'react';

import type { EventsByFeature } from '@evolvemep/api-ts';
import type { AgChartOptions } from 'ag-charts-enterprise';

import { isNotNil } from 'helpers/isNotNil';
import { ChartRenderer } from 'modules/Shop/Insights/RevitUsage/components/ChartRenderer';
import type { AnalyticsChartProps } from 'modules/Shop/Insights/RevitUsage/types';
import { useChartFilters } from 'modules/Shop/Insights/RevitUsage/useChartFilters';
import { useAnalyticsChart } from 'modules/Shop/Insights/RevitUsage/views/charts/useAnalyticsChart';

export const NumberOfEventsChart = ({ showDemo, ...props }: AnalyticsChartProps<EventsByFeature[]>) => {
  const filters = useChartFilters();
  const [data, loading] = useAnalyticsChart(`usage-stats/v1/reporting/events/features`, props);

  const maxDisplayRecords = useMemo(
    () => (props.usedFilters.includes('featureTypeNames') && filters.featureTypeNames.length > 0 ? undefined : 20),
    [filters.featureTypeNames.length, props.usedFilters],
  );

  const options = useMemo<AgChartOptions>(
    () => ({
      data: data?.toSorted((a, b) => b.totalUsage - a.totalUsage).slice(0, maxDisplayRecords),
      title: {
        enabled: !showDemo,
        text: 'Number of Events',
        textAlign: 'left',
      },
      subtitle: {
        text: `Limited to top ${maxDisplayRecords} features`,
        textAlign: 'left',
        enabled: isNotNil(maxDisplayRecords) && (data?.length ?? 0) > maxDisplayRecords && !showDemo,
      },
      series: [
        {
          type: 'bar',
          xKey: 'featureTypeName',
          yKey: 'totalUsage',
          yName: 'Total Events',
        },
      ],
      axes: {
        x: {
          type: 'category',
          position: 'bottom',
          label: {
            rotation: -35,
            minSpacing: 0,
          },
        },
        y: {
          type: 'number',
          position: 'left',
          label: {},
        },
      },
    }),
    [data, maxDisplayRecords, showDemo],
  );

  return <ChartRenderer options={options} loading={loading} />;
};
